import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"
import ChartDataLabels from "chartjs-plugin-datalabels"

// Opens a presentation-ready modal for the Distribution Trend multi-series bar
// chart. Layout mirrors chart_preview_controller: right-side vertical settings
// panel (Title alignment, Legend position, font-size sliders) and a bottom
// action bar with Copy, size presets, and dimensions display.
//
// Usage:
//   <button data-controller="distribution-trend-chart-modal"
//           data-distribution-trend-chart-modal-config-value="<%= cfg.to_json %>"
//           data-distribution-trend-chart-modal-title-value="Distribution by Cycle"
//           data-action="click->distribution-trend-chart-modal#open">
//     Expand
//   </button>
export default class extends Controller {
  static values = {
    config: Object,
    title:  { type: String, default: "Distribution Trend" }
  }

  static SIZES = [
    { name: "Square", width: 600,  height: 600 },
    { name: "Small",  width: 480,  height: 360 },
    { name: "Medium", width: 640,  height: 360 },
    { name: "Large",  width: 960,  height: 540 },
    { name: "Wide",   width: 1280, height: 540 },
    { name: "Tall",   width: 600,  height: 800 }
  ]

  static DEFAULT_SIZE_INDEX = 2 // Medium

  connect() {
    this._titleAlign     = "left"
    this._titleFontSize  = 20
    this._labelFontSize  = 12
    this._valueFontSize  = 14
    this._legendPosition = "bottom"
    this._dimensions     = { ...this.constructor.SIZES[this.constructor.DEFAULT_SIZE_INDEX] }
  }

  open(event) {
    if (event) event.stopPropagation()
    if (this.dialog) return

    this.dialog = this.buildDialog()
    document.body.appendChild(this.dialog)

    this.dialog.addEventListener("click", (e) => {
      if (e.target === this.dialog) this.close()
    })
    this.dialog.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close()
    })

    this.dialog.showModal()

    const canvas = this.dialog.querySelector("[data-chart-canvas]")
    if (canvas) this.chart = this.renderChart(canvas)
    this.applyTitleStyles()
  }

  close() {
    if (this.chart) { this.chart.destroy(); this.chart = null }
    if (this.dialog) { this.dialog.remove(); this.dialog = null }
  }

  // --- Settings handlers -----------------------------------------------------

  applyConfig(config) {
    if (config.startsWith("title-")) {
      this._titleAlign = config === "title-hide"
        ? "hidden"
        : (config === "title-centre" ? "center" : "left")
      this.applyTitleStyles()
      this.updateSegmentedActive(["title-left", "title-centre", "title-hide"], config)
    } else if (config.startsWith("legend-")) {
      this._legendPosition = config === "legend-hide"
        ? "hide"
        : (config === "legend-bottom" ? "bottom" : "right")
      this.updateSegmentedActive(["legend-right", "legend-bottom", "legend-hide"], config)
      this.redraw()
    }
  }

  updateSegmentedActive(keys, activeKey) {
    if (!this.dialog) return
    const activeClass   = "flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white"
    const inactiveClass = "flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100"
    keys.forEach((k) => {
      const btn = this.dialog.querySelector(`[data-config="${k}"]`)
      if (btn) btn.className = (k === activeKey) ? activeClass : inactiveClass
    })
  }

  setSliderFont(type, size) {
    if (type === "title") {
      this._titleFontSize = size
      this.applyTitleStyles()
    } else if (type === "label") {
      this._labelFontSize = size
      this.redraw()
    } else if (type === "value") {
      this._valueFontSize = size
      this.redraw()
    }
  }

  applySize(sizeIndex) {
    const size = this.constructor.SIZES[sizeIndex]
    if (!size) return
    this._dimensions = { width: size.width, height: size.height }

    // Resize the canvas container
    const container = this.dialog?.querySelector("[data-chart-container]")
    if (container) {
      container.style.width  = `${size.width}px`
      container.style.height = `${size.height}px`
    }

    // Sync the underlying canvas element so the exported PNG matches dimensions
    const canvas = this.dialog?.querySelector("[data-chart-canvas]")
    if (canvas) {
      canvas.width  = size.width
      canvas.height = size.height
    }

    // Update dimensions readout
    const label = this.dialog?.querySelector("[data-size-label]")
    if (label) label.textContent = `${size.width}x${size.height}`

    // Update preset button styling
    this.dialog?.querySelectorAll("[data-size-index]").forEach((btn, i) => {
      btn.className = (i === sizeIndex)
        ? "px-2 py-1 text-xs font-medium rounded-md transition-colors bg-sapphire text-white"
        : "px-2 py-1 text-xs font-medium rounded-md transition-colors bg-gray-100 text-gray-600 hover:bg-gray-200"
    })

    this.redraw()
  }

  redraw() {
    if (!this.dialog) return
    if (this.chart) { this.chart.destroy(); this.chart = null }
    const canvas = this.dialog.querySelector("[data-chart-canvas]")
    if (canvas) this.chart = this.renderChart(canvas)
  }

  applyTitleStyles() {
    if (!this.dialog) return
    const wrap    = this.dialog.querySelector("[data-chart-title]")
    const heading = this.dialog.querySelector("[data-chart-title] h3")
    if (!wrap || !heading) return

    if (this._titleAlign === "hidden") {
      wrap.style.display = "none"
      return
    }
    wrap.style.display   = ""
    wrap.style.textAlign = this._titleAlign
    heading.style.fontSize = `${this._titleFontSize}px`
  }

  // --- Copy ------------------------------------------------------------------

  async copy() {
    const chartCanvas = this.dialog?.querySelector("[data-chart-canvas]")
    if (!chartCanvas) return

    const button = this.dialog.querySelector("[data-copy-button]")

    try {
      const composite = this.compositeWithTitle(chartCanvas)
      const blob = await new Promise(resolve => composite.toBlob(resolve, "image/png"))
      await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
      this.flashCopied(button, "Copied!")
    } catch (err) {
      try {
        const blob = await new Promise(resolve => chartCanvas.toBlob(resolve, "image/png"))
        await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })])
        this.flashCopied(button, "Copied chart!")
      } catch (e) {
        const url = chartCanvas.toDataURL("image/png")
        window.open(url, "_blank")
      }
    }
  }

  flashCopied(button, message) {
    if (!button) return
    const original = button.innerHTML
    button.innerHTML = `<span>${message}</span>`
    button.classList.add("bg-green-600")
    setTimeout(() => {
      button.innerHTML = original
      button.classList.remove("bg-green-600")
    }, 2000)
  }

  // Composite the chart canvas with the title onto a single offscreen canvas,
  // sized to honour _dimensions so the exported PNG matches what's on screen.
  compositeWithTitle(chartCanvas) {
    const showTitle = this._titleAlign !== "hidden"
    const padding = 32
    const titleHeight = showTitle ? Math.max(40, this._titleFontSize * 2.2) : 0

    const chartWidth  = this._dimensions.width
    const chartHeight = this._dimensions.height
    const totalWidth  = chartWidth + (padding * 2)
    const totalHeight = chartHeight + titleHeight + (padding * 2)

    const composite = document.createElement("canvas")
    composite.width  = totalWidth
    composite.height = totalHeight
    const ctx = composite.getContext("2d")

    ctx.fillStyle = "#ffffff"
    ctx.fillRect(0, 0, totalWidth, totalHeight)

    if (showTitle) {
      ctx.fillStyle = "#111827"
      ctx.font = `bold ${this._titleFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif`
      ctx.textAlign = this._titleAlign === "center" ? "center" : "left"
      const titleY = padding + this._titleFontSize
      const titleX = this._titleAlign === "center" ? totalWidth / 2 : padding
      ctx.fillText(this.titleValue, titleX, titleY)
    }

    ctx.drawImage(chartCanvas, padding, titleHeight + padding, chartWidth, chartHeight)
    return composite
  }

  renderChart(canvas) {
    const cfg = this.configValue || {}
    const isPercent = !!cfg.is_percent

    const datasets = (cfg.datasets || []).map((ds) => ({
      borderRadius: { topLeft: 6, topRight: 6, bottomLeft: 0, bottomRight: 0 },
      borderSkipped: false,
      ...ds
    }))

    const legendHidden = this._legendPosition === "hide"

    return new Chart(canvas, {
      type: "bar",
      plugins: [ChartDataLabels],
      data: {
        labels: cfg.labels || [],
        datasets: datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: {
            display: !legendHidden,
            position: legendHidden ? "bottom" : this._legendPosition,
            labels: { font: { size: this._labelFontSize }, padding: 16 }
          },
          tooltip: {
            callbacks: isPercent ? {
              label: (ctx) => `${ctx.dataset.label}: ${Number(ctx.parsed.y).toFixed(1)}%`
            } : {}
          },
          datalabels: {
            anchor: "end",
            align: "start",
            offset: 4,
            color: "#ffffff",
            font: { weight: "bold", size: this._valueFontSize },
            display: (ctx) => {
              const v = ctx.dataset.data[ctx.dataIndex]
              return v !== null && v !== undefined && v !== 0
            },
            formatter: (value) => {
              if (value === null || value === undefined) return ""
              return isPercent
                ? `${Number(value).toFixed(1)}%`
                : Number(value).toLocaleString()
            }
          }
        },
        scales: {
          x: {
            grid: { display: false },
            ticks: { font: { size: this._labelFontSize } }
          },
          y: {
            beginAtZero: true,
            grid: { display: false },
            ticks: { display: false, font: { size: this._labelFontSize } }
          }
        }
      }
    })
  }

  buildDialog() {
    const defaultSize = this._dimensions

    const dialog = document.createElement("dialog")
    dialog.className = "p-0 rounded-lg m-auto backdrop:bg-black/80"
    dialog.style.maxWidth = "95vw"
    dialog.style.width = "max-content"

    const sizeTabs = this.constructor.SIZES.map((s, i) => {
      const active = (i === this.constructor.DEFAULT_SIZE_INDEX)
      return `<button type="button" data-size-index="${i}"
                class="px-2 py-1 text-xs font-medium rounded-md transition-colors ${active ? 'bg-sapphire text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}">${s.name}</button>`
    }).join("")

    const activeSeg   = "flex-1 px-2 py-1.5 text-xs font-medium bg-sapphire text-white"
    const inactiveSeg = "flex-1 px-2 py-1.5 text-xs font-medium bg-white text-gray-600 hover:bg-gray-100"

    dialog.innerHTML = `
      <div class="bg-white rounded-lg flex flex-col" style="width: max-content;">
        <div class="flex flex-nowrap">
          <!-- Snippable area: title + chart -->
          <div data-snippable class="border border-gray-200 rounded-tl-lg overflow-hidden bg-white shrink-0">
            <div data-chart-title class="px-6 pt-6 pb-2" style="text-align: left;">
              <h3 class="font-semibold text-gray-900" style="font-size: 20px;">${this.escapeHtml(this.titleValue)}</h3>
            </div>
            <div class="px-6 pb-6">
              <div data-chart-container style="position:relative; width:${defaultSize.width}px; height:${defaultSize.height}px;">
                <canvas data-chart-canvas width="${defaultSize.width}" height="${defaultSize.height}"></canvas>
              </div>
            </div>
          </div>

          <!-- Right-side config panel -->
          <div class="w-48 shrink-0 border-t border-r border-gray-200 bg-gray-50 p-3 flex flex-col gap-3 rounded-tr-lg overflow-y-auto">
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Title</label>
              <div class="flex rounded-md overflow-hidden border border-gray-200">
                <button type="button" data-config="title-left"   class="${activeSeg}">Left</button>
                <button type="button" data-config="title-centre" class="${inactiveSeg}">Centre</button>
                <button type="button" data-config="title-hide"   class="${inactiveSeg}">Hide</button>
              </div>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Legend</label>
              <div class="flex rounded-md overflow-hidden border border-gray-200">
                <button type="button" data-config="legend-right"  class="${inactiveSeg}">Right</button>
                <button type="button" data-config="legend-bottom" class="${activeSeg}">Bottom</button>
                <button type="button" data-config="legend-hide"   class="${inactiveSeg}">Hide</button>
              </div>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Title Size</label>
              <input type="range" data-font="title" min="12" max="32" value="20" class="w-full h-1.5 accent-sapphire">
              <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>12</span><span data-font-label="title">20px</span><span>32</span></div>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Label Size</label>
              <input type="range" data-font="label" min="8" max="20" value="12" class="w-full h-1.5 accent-sapphire">
              <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>8</span><span data-font-label="label">12px</span><span>20</span></div>
            </div>
            <div>
              <label class="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Value Size</label>
              <input type="range" data-font="value" min="8" max="24" value="14" class="w-full h-1.5 accent-sapphire">
              <div class="flex justify-between text-xs text-gray-400 mt-0.5"><span>8</span><span data-font-label="value">14px</span><span>24</span></div>
            </div>
          </div>
        </div>

        <!-- Bottom action bar -->
        <div class="border-t border-gray-200 px-6 py-3 flex items-center justify-between gap-4">
          <button type="button"
                  data-copy-button
                  class="inline-flex items-center gap-2 px-4 py-2 bg-blue-900 text-white text-sm font-medium rounded-lg hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-600 transition-colors shrink-0">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            Copy
          </button>
          <div class="flex items-center gap-1.5" data-size-tabs>
            ${sizeTabs}
          </div>
          <div class="flex items-center gap-3 shrink-0">
            <span data-size-label class="text-xs text-gray-400">${defaultSize.width}x${defaultSize.height}</span>
            <button type="button" data-close-button class="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
      </div>
    `

    dialog.querySelector("[data-close-button]").addEventListener("click", () => this.close())
    dialog.querySelector("[data-copy-button]").addEventListener("click", () => this.copy())

    // Segmented config toggles
    dialog.querySelectorAll("[data-config]").forEach((btn) => {
      btn.addEventListener("click", () => this.applyConfig(btn.dataset.config))
    })

    // Size preset buttons
    dialog.querySelectorAll("[data-size-index]").forEach((btn) => {
      btn.addEventListener("click", () => this.applySize(parseInt(btn.dataset.sizeIndex, 10)))
    })

    // Font-size sliders
    dialog.querySelectorAll("[data-font]").forEach((slider) => {
      slider.addEventListener("input", () => {
        const type = slider.dataset.font
        const size = parseInt(slider.value, 10)
        const label = dialog.querySelector(`[data-font-label="${type}"]`)
        if (label) label.textContent = `${size}px`
        this.setSliderFont(type, size)
      })
    })

    return dialog
  }

  escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c => ({ "&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;" }[c]))
  }
}
